import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Printer, CreditCard, MonitorCog } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { EmptyState } from '@/components/pos/EmptyState';
import { useDeviceBinding } from '@/hooks/queries';
import { useDeviceOptions } from '@/hooks/queries/useDevices';
import { useBindDevice } from '@/hooks/mutations';
import { useDeviceStore } from '@/stores/device.store';
import { useAuthStore } from '@/stores/auth.store';
import { env } from '@/lib/env';
import type { ApiError } from '@/types/api';

const NONE = '__none__';

export function DeviceSetupPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data: binding, isLoading: bindingLoading } = useDeviceBinding();
  const { data: printers = [], isLoading: printersLoading } = useDeviceOptions('TICKET_PRINTER');
  const { data: readers = [], isLoading: readersLoading } = useDeviceOptions('CASHLESS_READER');
  const { mutate: bind, isPending } = useBindDevice();
  const storeReader = useDeviceStore((s) => s.reader);

  const [name, setName] = useState('');
  const [printerId, setPrinterId] = useState<string>(NONE);
  const [readerId, setReaderId] = useState<string>(NONE);
  const [mid, setMid] = useState(storeReader.mid);
  const [tid, setTid] = useState(storeReader.tid);

  useEffect(() => {
    if (binding) {
      setName(binding.name ?? `Loket ${user?.name ?? ''}`.trim());
      setPrinterId(binding.ticketPrinterId ?? NONE);
      setReaderId(binding.cashlessReaderId ?? NONE);
      if (binding.reader) {
        setMid(binding.reader.mid);
        setTid(binding.reader.tid);
      }
    } else if (!name) {
      setName(`Loket ${user?.name ?? user?.email ?? ''}`.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binding]);

  const save = () => {
    if (printerId === NONE) {
      toast.error('Pilih printer tiket terlebih dahulu — wajib untuk mencetak tiket.');
      return;
    }
    bind(
      {
        name: name.trim() || 'Loket Ticketbox',
        type: 'TICKETBOX',
        ticketPrinterId: printerId === NONE ? null : printerId,
        cashlessReaderId: readerId === NONE ? null : readerId,
      },
      {
        onSuccess: () => {
          // mid/tid are carried locally for the cashless socket reader payload.
          useDeviceStore.setState({ reader: { mid: mid.trim(), tid: tid.trim() } });
          toast.success('Perangkat terikat. Selamat bekerja!');
          navigate('/', { replace: true });
        },
        onError: (err) => toast.error((err as ApiError)?.message ?? 'Gagal menyimpan perangkat.'),
      },
    );
  };

  const loading = bindingLoading || printersLoading || readersLoading;
  const noPrinters = !printersLoading && printers.length === 0;

  return (
    <div className="mx-auto h-full max-w-2xl overflow-y-auto px-6 py-10 pos-scroll">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <MonitorCog className="h-6 w-6" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-900">Atur Perangkat Loket</h1>
          <p className="text-sm text-muted-foreground">
            Ikat printer tiket dan pembaca kartu ke terminal ini.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-ink-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : noPrinters ? (
        <EmptyState
          icon={Printer}
          title="Tidak ada printer tersedia"
          description="Belum ada printer tiket yang terdaftar. Hubungi administrator untuk menambahkan perangkat sebelum berjualan."
        />
      ) : (
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>Identitas loket</CardTitle>
              <CardDescription>Nama untuk mengenali terminal ini di laporan.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                <Label htmlFor="dev-name">Nama loket</Label>
                <Input id="dev-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Loket A1" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Printer className="h-5 w-5 text-primary" /> Printer tiket
                <span className="text-destructive">*</span>
              </CardTitle>
              <CardDescription>Wajib — tiket dicetak melalui printer ini.</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={printerId} onValueChange={setPrinterId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih printer" />
                </SelectTrigger>
                <SelectContent>
                  {printers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" /> Pembaca kartu (e-money)
              </CardTitle>
              <CardDescription>Opsional — hanya untuk pembayaran e-money.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={readerId} onValueChange={setReaderId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih pembaca kartu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Tidak ada</SelectItem>
                  {readers.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="mid">Reader MID</Label>
                  <Input id="mid" value={mid} onChange={(e) => setMid(e.target.value)} className="tnum" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tid">Reader TID</Label>
                  <Input id="tid" value={tid} onChange={(e) => setTid(e.target.value)} className="tnum" />
                </div>
              </div>
              <p className="text-xs text-ink-400">
                Default dari konfigurasi: MID {env.CASHLESS_READER_MID || '—'} · TID {env.CASHLESS_READER_TID || '—'}.
              </p>
            </CardContent>
          </Card>

          <Button size="lg" className="h-14 w-full text-lg" onClick={save} disabled={isPending}>
            {isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Simpan & Mulai Berjualan'}
          </Button>
        </div>
      )}
    </div>
  );
}
